/**
 * Classes Page
 * List and manage all classes
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useViewMode } from '@/contexts/ViewModeContext';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Input } from '@/components/common/Input';
import { ViewModeToggle } from '@/components/common/ViewModeToggle';
import { apiRequest } from '@/api/client';

interface Class {
  classId: string;
  className: string;
  gradeLevel: string;
  studentCount: number;
  teacherName: string;
  ageRating: string;
  createdAt: string;
}

export const ClassesPage = () => {
  const { user } = useAuth();
  const { viewMode } = useViewMode();
  const navigate = useNavigate();
  const [classes, setClasses] = useState<Class[]>([]);
  const [filteredClasses, setFilteredClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchClasses();
  }, [viewMode]);

  useEffect(() => {
    if (searchTerm) {
      setFilteredClasses(
        classes.filter(c =>
          c.className.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.gradeLevel.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredClasses(classes);
    }
  }, [searchTerm, classes]);

  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      
      let response;
      if (user?.isAdmin && viewMode === 'school') {
        // Admin viewing all school classes
        response = await apiRequest<{ classes: Class[] }>('get_school_classes', {
          schoolId: user?.schoolId,
        });
      } else {
        // Personal classes
        response = await apiRequest<{ classes: Class[] }>('get_teacher_classes', {
          userEmail: user?.email,
        });
      }

      if (response.success && (response as any).classes) {
        setClasses((response as any).classes);
        setFilteredClasses((response as any).classes);
      }
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Classes</h1>
          <p className="text-slate-600 mt-2">Manage your classes and students</p>
        </div>
        <div className="flex items-center gap-3">
          <ViewModeToggle />
          <Button variant="primary" onClick={() => navigate('/classes/new')}>
            ➕ Create Class
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="p-4">
          <Input
            type="text"
            placeholder="Search classes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </Card>

      {/* Classes Grid */}
      {filteredClasses.length === 0 ? (
        <Card>
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">📚</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {searchTerm ? 'No classes found' : 'No classes yet'}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchTerm
                ? 'Try adjusting your search'
                : 'Create your first class to get started'}
            </p>
            {!searchTerm && (
              <Button variant="primary" onClick={() => navigate('/classes/new')}>
                Create Your First Class
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClasses.map((classItem) => (
            <Card key={classItem.classId}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {classItem.className}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      {classItem.gradeLevel}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                    {classItem.ageRating}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-slate-600">
                    <span className="mr-2">👥</span>
                    <span>{classItem.studentCount} students</span>
                  </div>
                  <div className="flex items-center text-sm text-slate-600">
                    <span className="mr-2">👨‍🏫</span>
                    <span>{classItem.teacherName}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/classes/${classItem.classId}`)}
                    className="flex-1"
                  >
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/classes/${classItem.classId}/edit`)}
                    className="flex-1"
                  >
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
